import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import { FaqAccordion } from "../components/shared/FaqAccordion";
import { logEvent } from "../utils/analytics";

const plans = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    sub: "forever",
    limit: "10 sims / month",
    cta: "Start free",
    ctaTo: "/3d-cargo-load-simulator",
    highlight: false,
    features: [
      "10 simulations per month",
      "Hand-stuffed & palletized modes",
      "Full 3D interactive viewer",
      "Container selection optimisation",
      "Cost analysis & utilisation report",
      "Simulation history (30 days)",
    ],
    missing: ["API access", "Shareable links", "Priority support"],
  },
  {
    id: "starter",
    name: "Starter",
    price: "$29",
    sub: "/month",
    limit: "100 sims / month",
    cta: "Contact us",
    ctaTo: "/contact-sales",
    highlight: false,
    features: [
      "100 simulations per month",
      "Everything in Free",
      "Shareable result links",
      "Simulation history (unlimited)",
      "Email support",
    ],
    missing: ["API access", "Priority support"],
  },
  {
    id: "growth",
    name: "Growth",
    price: "$99",
    sub: "/month",
    limit: "1,000 sims / month",
    cta: "Contact us",
    ctaTo: "/contact-sales",
    highlight: true,
    badge: "Most popular",
    features: [
      "1,000 simulations per month",
      "Everything in Starter",
      "API access (REST)",
      "Priority email support",
      "Custom container templates",
    ],
    missing: [],
  },
  {
    id: "lifetime",
    name: "Lifetime",
    price: "$299",
    sub: "one-time",
    limit: "200 sims / month, forever",
    cta: "Contact us",
    ctaTo: "/contact-sales",
    highlight: false,
    badge: "Best value",
    features: [
      "200 simulations / month, forever",
      "Everything in Starter",
      "No renewal, no price increases",
      "Shareable result links",
      "Email support",
    ],
    missing: ["API access", "Priority support"],
  },
];

const faqs = [
  {
    id: "what-counts-as-simulation",
    question: "What counts as one simulation?",
    answer: "Each time you click 'Run Simulation' and the engine returns a result counts as one simulation. Viewing or forking past simulations does not count.",
  },
  {
    id: "unused-simulations-rollover",
    question: "Do unused simulations roll over?",
    answer: "No. The monthly counter resets on the 1st of each month. Unused simulations do not carry forward.",
  },
  {
    id: "try-before-paying",
    question: "Can I try before paying?",
    answer: "Yes. The Free plan gives you 10 simulations per month with no card required. You can evaluate the full product quality before deciding to upgrade.",
  },
  {
    id: "api-plan",
    question: "What is the API plan?",
    answer: "The API plan is for platforms, TMS providers, and logistics SaaS companies who want to embed container planning into their own product. Pricing is custom based on call volume. Talk to us.",
  },
  {
    id: "lifetime-forever",
    question: "Is the Lifetime plan really forever?",
    answer: "Yes. One payment, 200 simulations/month, no expiry. If we ever change our pricing model we grandfather all Lifetime users.",
  },
  {
    id: "cbm-calculator-no-account",
    question: "Can I use the CBM calculator without an account?",
    answer: "Absolutely. The CBM calculator at feuteuristix.com/cbm-calculator is completely free with no signup required.",
  },
];

export function PricingPage() {
  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Pricing — Container Load Planning Software | Feuteuristix</title>
        <meta name="description" content="Start free with 10 simulations/month. Upgrade to Starter, Growth, or Lifetime plans. API access for logistics platforms and TMS providers." />
        <meta property="og:title" content="Pricing — Container Load Planning Software | Feuteuristix" />
        <meta property="og:description" content="Start free with 10 simulations/month. Upgrade to Starter, Growth, or Lifetime plans. API access for logistics platforms and TMS providers." />
        <meta property="og:url" content="https://feuteuristix.com/pricing" />
        <meta name="twitter:title" content="Pricing — Container Load Planning Software | Feuteuristix" />
        <meta name="twitter:description" content="Start free with 10 simulations/month. Upgrade to Starter, Growth, or Lifetime plans. API access for logistics platforms and TMS providers." />
        <link rel="canonical" href="https://feuteuristix.com/pricing" />
      </Helmet>
      <SiteHeader />

      <main className="flex-1 pt-24 pb-16">
        {/* Header */}
        <section className="py-14 text-center">
          <div className="mx-auto max-w-3xl px-6">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              Pricing
            </span>
            <h1 className="mt-4 text-4xl font-black leading-tight md:text-5xl">
              Start free. Scale when you need to.
            </h1>
            <p className="mt-4 text-lg text-slate-300 max-w-xl mx-auto">
              10 free simulations every month, no card required. Upgrade only when the savings justify it — and they will.
            </p>
          </div>
        </section>

        {/* Plans grid */}
        <section className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border p-7 ${
                  plan.highlight
                    ? "border-primary/50 bg-gradient-to-b from-primary/10 to-transparent shadow-2xl shadow-primary/20"
                    : "border-white/10 bg-white/[0.04]"
                }`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-black ${plan.highlight ? "bg-primary text-white" : "bg-white/10 text-slate-300"}`}>
                    {plan.badge}
                  </div>
                )}

                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{plan.name}</p>
                  <div className="mt-2 flex items-end gap-1">
                    <span className="text-4xl font-black text-white">{plan.price}</span>
                    <span className="pb-1 text-sm text-slate-400">{plan.sub}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-primary">{plan.limit}</p>
                </div>

                {/* CTA */}
                <Link
                  to={plan.ctaTo}
                  onClick={() => logEvent({ name: 'pricing_cta', type: 'cta', meta: { plan: plan.id } })}
                  className={`mb-6 flex h-11 w-full items-center justify-center rounded-xl font-bold text-sm transition ${
                    plan.highlight
                      ? "bg-gradient-to-r from-primary to-blue-500 text-white hover:brightness-110"
                      : "border border-white/15 bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  {plan.cta}
                </Link>

                {/* Features */}
                <div className="flex flex-col gap-2.5">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <span className="material-symbols-outlined mt-0.5 text-[14px] text-emerald-400 shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                      {f}
                    </div>
                  ))}
                  {plan.missing.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="material-symbols-outlined mt-0.5 text-[14px] shrink-0">remove</span>
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Enterprise / API callout */}
          <div className="mt-8 flex flex-col items-center justify-between gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:flex-row">
            <div>
              <p className="text-lg font-black text-white">Building a logistics platform or TMS?</p>
              <p className="mt-1 text-sm text-slate-400 max-w-lg">
                Embed Feuteuristix container intelligence into your own product via our REST API. Custom pricing based on call volume. We handle the integration end-to-end.
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <Link to="/developers" className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition">
                View API docs
              </Link>
              <Link
                to="/contact-sales"
                onClick={() => logEvent({ name: 'pricing_talk_to_us', type: 'cta' })}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-5 py-2.5 text-sm font-bold text-white hover:brightness-110 transition"
              >
                Talk to us
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-6 md:px-8 py-16">
          <h2 className="mb-8 text-2xl font-black text-white text-center">Frequently asked questions</h2>
          <FaqAccordion items={faqs} />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
